/**
 * Dashboard Page
 * 
 * Main dashboard interface for the Prelude application.
 * Features include:
 * - Overview of upcoming services
 * - Quick access to recent songs
 * - Team participation statistics
 * - System notifications and alerts
 * - Quick action shortcuts
 * 
 * Provides a centralized view of important information
 * and quick access to frequently used features.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Skeleton,
  Alert,
  Chip,
  Stack,
  Grid,
  useTheme as useMuiTheme,
  useMediaQuery,
  Button,
  Tooltip,
  Collapse,
  CardActions,
  Link,
  Avatar,
  Paper
} from '@mui/material';
import {
  Event as EventIcon,
  LibraryMusic as MusicNoteIcon,
  Group as GroupIcon,
  MusicNote as RehearsalIcon,
  Church as ServiceIcon,
  Dashboard as DashboardIcon,
  QueueMusic as QueueMusicIcon,
  Timer as TimerIcon,
  Tune as TuneIcon,
  GraphicEq as GraphicEqIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Settings as SettingsIcon,
  CalendarToday as CalendarTodayIcon,
  MusicVideo as CapoIcon,
  ContentCopy,
  AccessTime as AccessTimeIcon,
  InfoOutlined as InfoOutlinedIcon,
  Star as StarIcon,
  LibraryMusic as LibraryMusicIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { API_ENDPOINTS } from '../config/api';
import customFetch from '../utils/axios';
import { toast } from 'react-toastify';
import { formatDate, formatDateTime } from '../utils/dateFormatter';
import ServiceForm from '../components/services/ServiceForm';
import PageHeader from '../components/common/PageHeader';
import BirthdayCard from '../components/dashboard/BirthdayCard';
import ProUpgradeModal from '../components/common/ProUpgradeModal';

// Pending Service Warning Component
const PendingServiceWarning = ({ pendingServices, onAccept, onDecline }) => {
  const { t } = useTranslation();
  
  if (!pendingServices || pendingServices.length === 0) {
    return null;
  }
  
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 2,
        bgcolor: 'warning.light',
        color: 'warning.contrastText',
        border: '1px solid',
        borderColor: 'warning.main',
        borderRadius: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
            {pendingServices.length === 1
              ? t('dashboard.pendingServiceWarning.title.single')
              : t('dashboard.pendingServiceWarning.title.multiple', { count: pendingServices.length })}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {pendingServices.length === 1
              ? t('dashboard.pendingServiceWarning.message.single', { title: pendingServices[0].service.title })
              : t('dashboard.pendingServiceWarning.message.multiple')}
          </Typography>
        </Box>
      </Box>
      
      {pendingServices.map((serviceMember) => (
        <Box key={serviceMember._id} sx={{ mb: 2, p: 1, bgcolor: 'rgba(255, 255, 255, 0.1)', borderRadius: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
            {serviceMember.service.title} - {new Date(serviceMember.service.date).toLocaleDateString()}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {t('services.teamSection.position')}: {serviceMember.position}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Button
              variant="contained"
              color="success"
              size="small"
              onClick={() => onAccept(serviceMember._id, serviceMember.service._id)}
            >
              {t('common.accept')}
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={() => onDecline(serviceMember._id, serviceMember.service._id)}
            >
              {t('common.decline')}
            </Button>
          </Box>
        </Box>
      ))}
    </Paper>
  );
};

const DashboardCard = ({ title, icon, subtitle, onClick }) => {
  const theme = useMuiTheme();
  return (
    <Card
      onClick={onClick}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: theme.palette.background.paper,
        backdropFilter: 'blur(10px)',
        borderRadius: 2,
        border: '1px solid rgba(255, 255, 255, 0.12)',
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        cursor: 'pointer',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        },
      }}
    >
      <CardContent 
        sx={{ 
          flexGrow: 1, 
          position: 'relative',
          p: { xs: 1.5, sm: 2 }, 
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
        }}
      >
        <Box 
          sx={{ 
            color: 'primary.main',
            mb: { xs: 1, sm: 1.5 },
            '& > svg': {
              fontSize: { xs: '1.75rem', sm: '2.25rem' } 
            }
          }}
        >
          {icon}
        </Box>
        <Typography 
          variant="h6" 
          component="h2" 
          sx={{
            fontSize: { xs: '0.875rem', sm: '1.125rem' },
            mb: 0.5,
            fontWeight: 600,
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{
              fontSize: { xs: '0.75rem', sm: '0.875rem' }
            }}
          >
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

const StatCard = ({ title, value, icon, loading, error }) => {
  const theme = useMuiTheme();
  return (
    <Card
      sx={{
        height: '100%',
        background: theme.palette.background.paper,
        backdropFilter: 'blur(10px)',
        borderRadius: 2,
        border: '1px solid rgba(255, 255, 255, 0.12)',
      }}
    >
      <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          mb: { xs: 1, sm: 2 }
        }}>
          {icon}
          <Typography variant="h6" sx={{ ml: 1 }}>
            {title}
          </Typography>
        </Box>
        {loading ? (
          <Skeleton variant="text" width="60%" height={40} />
        ) : error ? (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        ) : (
          <Typography 
            variant="h4" 
            color="primary" 
            sx={{ 
              fontWeight: 'bold',
              fontSize: { xs: '1.5rem', sm: '2.125rem' } 
            }}
          >
            {value}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

const TopSongsCard = ({ songs, loading, error, t }) => {
  const theme = useMuiTheme();
  const navigate = useNavigate();

  const handleSongClick = (song) => {
    navigate(`/app/songs/${song._id}`);
  };

  return (
    <Card
      sx={{
        height: '100%',
        background: theme.palette.background.paper,
        backdropFilter: 'blur(10px)',
        borderRadius: 2,
        border: '1px solid rgba(255, 255, 255, 0.12)',
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <QueueMusicIcon color="primary" />
          <Typography variant="h6" sx={{ ml: 1 }}>
            {t('dashboard.mostUsedSongs')}
          </Typography>
        </Box>
        {loading ? (
          [...Array(3)].map((_, i) => (
            <Box key={i} sx={{ mb: 2 }}>
              <Skeleton variant="text" width="100%" />
            </Box>
          ))
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : songs.length === 0 ? (
          <Typography color="text.secondary">{t('dashboard.noSongs')}</Typography>
        ) : (
          <List sx={{ p: 0 }}>
            {songs.map((song, index) => (
              <React.Fragment key={song._id}>
                {index > 0 && <Divider sx={{ my: 1 }} />}
                <ListItem
                  sx={{
                    px: 0,
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                    transition: 'background-color 0.2s',
                    borderRadius: 1,
                  }}
                  onClick={() => handleSongClick(song)}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Typography color="primary" variant="body2">
                      {index + 1}
                    </Typography>
                  </ListItemIcon>
                  <ListItemText 
                    primary={song.title}
                    secondary={song.author ? song.author : null}
                    primaryTypographyProps={{ noWrap: true }}
                    secondaryTypographyProps={{ 
                      noWrap: true,
                      variant: 'body2',
                      color: 'text.secondary',
                      fontSize: '0.75rem'
                    }}
                    sx={{ mr: 1 }}
                  />
                  <Chip
                    label={`${song.usageCount}x`}
                    size="small"
                    sx={{
                      bgcolor: 'primary.main',
                      color: 'white',
                      fontSize: '0.75rem',
                      flexShrink: 0
                    }}
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </CardContent>
    </Card>
  );
};

const TimeInfo = ({ icon, label, date, time }) => {
  if (!date) return null;

  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) return null;

  // Create a full date-time string for proper formatting
  let dateTimeObj = new Date(dateObj);
  if (time && time.start) {
    const [hours, minutes] = time.start.split(':');
    dateTimeObj.setHours(parseInt(hours), parseInt(minutes));
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
      {icon}
      <Typography variant="body2" color="text.secondary">
        {label}: {formatDateTime(dateTimeObj)}
      </Typography>
    </Box>
  );
};

const ServiceCard = ({ service }) => {
  const { t } = useTranslation();
  const theme = useMuiTheme();

  return (
    <Card sx={{ mb: 2, p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Typography variant="h6" component="div">
          {service.title || t('dashboard.untitledService')}
        </Typography>
        <Chip
          label={service.category || t('dashboard.uncategorized')}
          size="small"
          sx={{ bgcolor: 'primary.main', color: 'white' }}
        />
      </Box>

      <TimeInfo
        icon={<ServiceIcon sx={{ color: theme.palette.primary.main }} />}
        label={t('dashboard.service')}
        date={service.date}
        time={service.time}
      />

      {service.rehearsal && service.rehearsal.date && (
        <TimeInfo
          icon={<RehearsalIcon sx={{ color: theme.palette.primary.main }} />}
          label={t('dashboard.rehearsal')}
          date={service.rehearsal.date}
          time={service.rehearsal.time}
        />
      )}

      <Box sx={{ mt: 1 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {service.team?.length || 0} {t('dashboard.members')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {service.songs?.length || 0} {t('dashboard.songs')}
          </Typography>
        </Box>
      </Box>
    </Card>
  );
};

const UpcomingServices = ({ upcomingServices, loading, error, teamSchedule = [] }) => {
  const [expanded, setExpanded] = useState(false);
  const theme = useMuiTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const getServiceMembers = (serviceId) => {
    // Filter the teamSchedule to get members for this specific service
    if (!teamSchedule || !teamSchedule.length) return [];
    return teamSchedule.filter(member => member.service._id === serviceId);
  };

  const getUserPosition = (service) => {
    if (!service?.assignments) return null;
    
    const userAssignment = service.assignments.find(assignment => 
      assignment.userId === user._id
    );
    
    return userAssignment?.position;
  };

  return (
    <Card sx={{ height: '100%', bgcolor: theme.palette.mode === 'dark' ? '#1A1A1A' : 'white', overflow: 'hidden' }}>
      <CardContent sx={{ p: 2, overflow: 'hidden' }}>
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            mb: 2,
            cursor: 'pointer'
          }}
          onClick={handleExpandClick}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <EventIcon color="primary" />
            <Typography variant="h6" sx={{ ml: 1 }}>
              {t('dashboard.upcomingServices')}
            </Typography>
          </Box>
          <IconButton
            aria-expanded={expanded}
            aria-label="show more"
            size="small"
            onClick={(e) => {
              e.stopPropagation(); // Prevent triggering the parent onClick
              handleExpandClick();
            }}
          >
            <ExpandMoreIcon sx={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }} />
          </IconButton>
        </Box>

        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ width: '100%', overflow: 'hidden' }}>
            {loading ? (
              <Grid container spacing={2}>
                {[...Array(3)].map((_, i) => (
                  <Grid item xs={12} sm={6} md={4} key={i}>
                    <Skeleton 
                      variant="rectangular" 
                      width="100%"
                      height={150}
                      sx={{ borderRadius: 2 }}
                    />
                  </Grid>
                ))}
              </Grid>
            ) : error ? (
              <Alert severity="error">{error}</Alert>
            ) : upcomingServices.length === 0 ? (
              <Typography color="text.secondary" sx={{ mt: 2 }}>
                {t('dashboard.noUpcomingServices')}
              </Typography>
            ) : (
              <Box sx={{ width: '100%' }}>
                <Grid container spacing={2}>
                  {upcomingServices.map((service) => (
                    <Grid item xs={12} sm={6} md={4} key={service._id}>
                      <Link
                        component="button"
                        onClick={() => navigate(`/app/services/${service._id}`)}
                        sx={{
                          width: '100%',
                          textDecoration: 'none',
                          color: 'inherit',
                          '&:hover': {
                            textDecoration: 'none',
                            color: 'inherit'
                          }
                        }}
                      >
                        <Card
                          sx={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            bgcolor: theme.palette.mode === 'dark' ? '#2A2A2A' : '#f8f9fa',
                            borderRadius: 2,
                            boxShadow: 3,
                            position: 'relative',
                            overflow: 'hidden'
                          }}
                        >
                          {/* Colored top bar based on service type */}
                          <Box
                            sx={{
                              height: '8px',
                              width: '100%',
                              bgcolor: service.type === 'worship' ? 'primary.main' : 
                                      service.type === 'rehearsal' ? 'secondary.main' : 
                                      service.type === 'special' ? 'error.main' : 'info.main',
                            }}
                          />
                          
                          <CardContent sx={{ flexGrow: 1, pt: 2 }}>
                            <Typography variant="h6" gutterBottom noWrap>
                              {service.title}
                            </Typography>
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRadius: '50%',
                                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                                  width: 32,
                                  height: 32,
                                  mr: 1,
                                  flexShrink: 0
                                }}
                              >
                                <AccessTimeIcon fontSize="small" color="action" />
                              </Box>
                              <Typography variant="body2" color="text.secondary">
                                {formatDateTime(service.date)}
                              </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRadius: '50%',
                                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                                  width: 32,
                                  height: 32,
                                  mr: 1,
                                  flexShrink: 0
                                }}
                              >
                                <InfoOutlinedIcon fontSize="small" color="action" />
                              </Box>
                              <Typography variant="body2" color="text.secondary" noWrap>
                                {service.description}
                              </Typography>
                            </Box>
                            
                            {/* Team members avatars */}
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, mt: 1 }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRadius: '50%',
                                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                                  width: 32,
                                  height: 32,
                                  mr: 1,
                                  flexShrink: 0
                                }}
                              >
                                <GroupIcon fontSize="small" color="action" />
                              </Box>
                              <Box sx={{ display: 'flex', overflow: 'hidden' }}>
                                {getServiceMembers(service._id).map((member, index) => (
                                  <Avatar
                                    key={member._id || index}
                                    src={member.user?.profilePhoto}
                                    alt={member.user?.firstName || member.user?.lastName}
                                    sx={{
                                      width: 28,
                                      height: 28,
                                      ml: index > 0 ? -1 : 0,
                                      border: '2px solid',
                                      borderColor: theme.palette.background.paper,
                                      bgcolor: theme.palette.primary.main,
                                      fontSize: '0.8rem'
                                    }}
                                  >
                                    {member.user?.firstName ? member.user.firstName.charAt(0) : '?'}
                                  </Avatar>
                                )).slice(0, 5)}
                                {getServiceMembers(service._id).length > 5 && (
                                  <Avatar
                                    sx={{
                                      width: 28,
                                      height: 28,
                                      ml: -1,
                                      border: '2px solid',
                                      borderColor: theme.palette.background.paper,
                                      bgcolor: theme.palette.grey[500],
                                      fontSize: '0.8rem'
                                    }}
                                  >
                                    +{getServiceMembers(service._id).length - 5}
                                  </Avatar>
                                )}
                              </Box>
                            </Box>
                            
                            {getUserPosition(service) && (
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '50%',
                                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                                    width: 32,
                                    height: 32,
                                    mr: 1,
                                    flexShrink: 0
                                  }}
                                >
                                  <MusicNoteIcon fontSize="small" color="primary" />
                                </Box>
                                <Typography variant="body2" color="primary" noWrap>
                                  <strong>{getUserPosition(service)}</strong>
                                </Typography>
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      </Link>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

const ServiceList = ({ services, loading, error, title, emptyMessage, t }) => {
  const theme = useMuiTheme();
  const navigate = useNavigate();

  const handleServiceClick = (service) => {
    navigate(`/app/services/${service._id}`);
  };

  return (
    <Card
      sx={{
        height: '100%',
        background: theme.palette.background.paper,
        backdropFilter: 'blur(10px)',
        borderRadius: 2,
        border: '1px solid rgba(255, 255, 255, 0.12)',
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <EventIcon color="primary" />
          <Typography variant="h6" sx={{ ml: 1 }}>
            {title}
          </Typography>
        </Box>
        {loading ? (
          [...Array(3)].map((_, i) => (
            <Box key={i} sx={{ mb: 2 }}>
              <Skeleton variant="text" width="100%" />
            </Box>
          ))
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : services.length === 0 ? (
          <Typography color="text.secondary">{emptyMessage}</Typography>
        ) : (
          <List sx={{ p: 0 }}>
            {services.map((service, index) => (
              <React.Fragment key={service._id}>
                {index > 0 && <Divider sx={{ my: 1 }} />}
                <ListItem 
                  sx={{ 
                    px: 0, 
                    flexDirection: 'column', 
                    alignItems: 'flex-start',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                    transition: 'background-color 0.2s',
                    borderRadius: 1,
                  }}
                  onClick={() => handleServiceClick(service)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', mb: 1 }}>
                    <ListItemText
                      primary={service.title}
                      sx={{ m: 0 }}
                    />
                    <Chip
                      label={service.category}
                      size="small"
                      sx={{
                        ml: 1,
                        bgcolor: 'primary.main',
                        color: 'white',
                        fontSize: '0.75rem'
                      }}
                    />
                  </Box>
                  <TimeInfo
                    icon={<RehearsalIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />}
                    label={t('dashboard.rehearsal')}
                    date={service.rehearsal.date}
                    time={service.rehearsal.time}
                  />
                  <TimeInfo
                    icon={<ServiceIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />}
                    label={t('dashboard.service')}
                    date={service.date}
                    time={service.time}
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </CardContent>
    </Card>
  );
};

const TeamScheduleCard = ({ schedule, loading, error }) => (
  <Card >
  </Card>
);

const TeamStatsCard = ({ stats, loading, error }) => (
  <Card>
  </Card>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, churchData } = useAuth();
  const theme = useMuiTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [services, setServices] = useState([]);
  const [upcomingServices, setUpcomingServices] = useState([]);
  const [recentServices, setRecentServices] = useState([]);
  const [pendingServices, setPendingServices] = useState([]);
  const [birthdays, setBirthdays] = useState([]);
  const [birthdaysLoading, setBirthdaysLoading] = useState(true);
  const [birthdaysError, setBirthdaysError] = useState(null);
  const [stats, setStats] = useState({
    teamSchedule: [],
    topSongs: [],
    teamStats: [],
    summary: {
      upcomingServices: 0,
      totalTeamMembers: 0,
      totalSongs: 0,
    },
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isServiceFormOpen, setIsServiceFormOpen] = useState(false);
  const [topSongsExpanded, setTopSongsExpanded] = useState(false);
  const [proModalOpen, setProModalOpen] = useState(false);
  const { t } = useTranslation();

  const fetchData = async () => {
    console.group('📊 Dashboard Data Fetching');
    try {
      setLoading(true);
      setError(null);
      console.log('🔄 Starting data fetch...');

      console.log('📡 Fetching dashboard stats...');
      const response = await customFetch.get(API_ENDPOINTS.STATS.DASHBOARD);
      console.log('📊 Dashboard stats response:', response);

      // The response.data contains the actual API response
      const apiResponse = response.data;
      if (!apiResponse?.success) {
        throw new Error(apiResponse?.message || 'Failed to fetch dashboard data');
      }

      // The actual data is nested under response.data.data
      const data = apiResponse.data || {
        teamSchedule: [],
        topSongs: [],
        teamStats: [],
        upcomingServices: [],
        recentServices: [],
        summary: {
          upcomingServices: 0,
          totalTeamMembers: 0,
          totalSongs: 0,
        }
      };

      const teamScheduleData = data.teamSchedule || [];
      
      // Filter pending services that need user response
      const pendingServiceMembers = teamScheduleData.filter(member => 
        member.user._id === user.userId && 
        member.status === 'pending'
      );
      
      setStats({
        teamSchedule: teamScheduleData,
        topSongs: data.topSongs || [],
        teamStats: data.teamStats || [],
        summary: data.summary || {
          upcomingServices: 0,
          totalTeamMembers: 0,
          totalSongs: 0,
        }
      });
      setUpcomingServices(data.upcomingServices || []);
      setRecentServices(data.recentServices || []);
      setPendingServices(pendingServiceMembers);
      
      console.log('✅ Data fetch complete');
    } catch (err) {
      console.error('❌ Error in dashboard data fetch:', err);
      setError(t('errors.dashboardDataFailed', 'Failed to load dashboard data'));
    } finally {
      setLoading(false);
      console.groupEnd();
    }
  };

  const fetchBirthdays = async () => {
    console.group('🎂 Birthdays Data Fetching');
    try {
      setBirthdaysLoading(true);
      setBirthdaysError(null);
      console.log('🔄 Fetching birthdays for current month...');

      const response = await customFetch.get(API_ENDPOINTS.USERS.BIRTHDAYS_THIS_MONTH);
      console.log('🎂 Birthdays response:', response);

      const apiResponse = response.data;
      if (!apiResponse?.success) {
        throw new Error(apiResponse?.message || 'Failed to fetch birthdays');
      }

      setBirthdays(apiResponse.data || []);
      console.log(`✅ Found ${apiResponse.data.length} birthdays this month`);
    } catch (err) {
      console.error('❌ Error fetching birthdays:', err);
      setBirthdaysError(t('errors.birthdaysDataFailed', 'Failed to load birthdays'));
    } finally {
      setBirthdaysLoading(false);
      console.groupEnd();
    }
  };

  const handleServiceSubmit = async (serviceData) => {
    try {
      const response = await customFetch.post(API_ENDPOINTS.SERVICES, serviceData);
      // After successful submission, refresh the dashboard data
      fetchData();
      setIsServiceFormOpen(false);
      toast.success(t('services.createSuccess'));
    } catch (error) {
      console.error('Error creating service:', error);
      toast.error(t('services.createError'));
    }
  };

  const handleServiceClick = useCallback((service) => {
    navigate(`/app/services/${service._id}`);
  }, [navigate]);

  const handleSongClick = useCallback((song) => {
    navigate(`/app/songs/${song._id}`);
  }, [navigate]);

  const handleAcceptService = async (serviceMemberId, serviceId) => {
    try {
      console.log(`Accepting service invitation for member ${serviceMemberId} in service ${serviceId}`);
      const response = await customFetch.put(
        API_ENDPOINTS.SERVICES.MEMBER_STATUS(serviceId, serviceMemberId),
        { status: 'confirmed' }
      );
      
      if (response.data.success) {
        toast.success(t('services.acceptSuccess', 'Service accepted successfully'));
        // Update the pending services list by removing the accepted one
        setPendingServices(prev => prev.filter(service => service._id !== serviceMemberId));
        // Refresh dashboard data
        fetchData();
      } else {
        toast.error(t('errors.serviceUpdateFailed', 'Failed to update service status'));
      }
    } catch (error) {
      console.error('Error accepting service:', error);
      toast.error(t('errors.serviceUpdateFailed', 'Failed to update service status'));
    }
  };

  const handleDeclineService = async (serviceMemberId, serviceId) => {
    try {
      console.log(`Declining service invitation for member ${serviceMemberId} in service ${serviceId}`);
      const response = await customFetch.put(
        API_ENDPOINTS.SERVICES.MEMBER_STATUS(serviceId, serviceMemberId),
        { status: 'declined' }
      );
      
      if (response.data.success) {
        toast.success(t('services.declineSuccess', 'Service declined'));
        // Update the pending services list by removing the declined one
        setPendingServices(prev => prev.filter(service => service._id !== serviceMemberId));
        // Refresh dashboard data
        fetchData();
      } else {
        toast.error(t('errors.serviceUpdateFailed', 'Failed to update service status'));
      }
    } catch (error) {
      console.error('Error declining service:', error);
      toast.error(t('errors.serviceUpdateFailed', 'Failed to update service status'));
    }
  };

  useEffect(() => {
    fetchData();
    fetchBirthdays();
  }, [t]);

  const cards = [
    {
      title: 'Services',
      icon: <EventIcon fontSize="large" />,
      subtitle: 'Plan and manage worship services',
      path: '/app/services',
    },
    {
      title: 'Songs',
      icon: <MusicNoteIcon fontSize="large" />,
      subtitle: 'Browse and manage song library',
      path: '/app/songs',
    },
    {
      title: 'People',
      icon: <GroupIcon fontSize="large" />,
      subtitle: 'Manage team members and schedules',
      path: '/app/people',
    },
  ];

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box>
        <Typography
          variant="h4"
          component="h1"
          title={churchData?.name ? `Prelude - ${churchData.name}` : 'Prelude'}
          sx={{ mb: 2, display: 'none' }}
        >
          {churchData?.name ? `Prelude - ${churchData.name}` : 'Prelude'}
        </Typography>
        <PageHeader 
          title={churchData?.name ? `Prelude - ${churchData.name}` : 'Prelude'}
          subtitle={t('common.welcome')}
          icon={<DashboardIcon />}
        />
      </Box>

      {/* Pending Service Warning */}
      <PendingServiceWarning
        pendingServices={pendingServices}
        onAccept={handleAcceptService}
        onDecline={handleDeclineService}
      />

      <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ mt: 1 }}>
        {/* Quick Access Cards */}
        <Grid item xs={4}>
          <DashboardCard
            title={t('nav.services')}
            icon={<EventIcon />}
            subtitle={`${stats.summary.upcomingServices || 0} ${t('dashboard.upcoming')}`}
            onClick={() => navigate('/app/services')}
          />
        </Grid>
        <Grid item xs={4}>
          <DashboardCard
            title={t('nav.songs')}
            icon={<MusicNoteIcon />}
            subtitle={`${stats.summary.totalSongs || 0} ${t('dashboard.total')}`}
            onClick={() => navigate('/app/songs')}
          />
        </Grid>
        <Grid item xs={4}>
          <DashboardCard
            title={t('nav.people')}
            icon={<GroupIcon />}
            subtitle={`${stats.summary.totalTeamMembers || 0} ${t('dashboard.members')}`}
            onClick={() => navigate('/app/people')}
          />
        </Grid>

        {/* Suggestions Button */}
        <Grid item xs={12} sx={{ mt: 1 }}>
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={() => {
              const isPro = user?.isPro || user?.subscription?.status === 'active';
              if (isPro) {
                navigate('/app/song-suggestions');
              } else {
                setProModalOpen(true);
              }
            }}
            startIcon={<LibraryMusicIcon />}
            endIcon={!(user?.isPro || user?.subscription?.status === 'active') ? <StarIcon sx={{ color: '#FFD700' }} /> : null}
            sx={{
              py: 2,
              background: 'linear-gradient(135deg, #a259ff 0%, #7c3aed 100%)',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '1rem',
              '&:hover': {
                background: 'linear-gradient(135deg, #8a3de0 0%, #6d28d9 100%)',
              },
            }}
          >
            {t('songSuggestions.title', 'Song Suggestions')}
          </Button>
        </Grid>

        {/* Tools Section */}
        <Grid item xs={12}>
          <Box sx={{ 
            display: 'flex', 
            gap: 2,
            flexDirection: 'row'
          }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={() => navigate('/app/metronome')}
              startIcon={<TimerIcon />}
              sx={{
                flex: 1,
                py: 2,
                backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1A1A1A' : 'white',
                color: (theme) => theme.palette.mode === 'dark' ? 'white' : 'primary.main',
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1A1A1A' : 'white',
                  opacity: 0.9,
                },
              }}
            >
              {t('dashboard.openMetronome')}
            </Button>

            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={() => navigate('/app/tuner')}
              startIcon={<GraphicEqIcon />}
              sx={{
                flex: 1,
                py: 2,
                backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1A1A1A' : 'white',
                color: (theme) => theme.palette.mode === 'dark' ? 'white' : 'primary.main',
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1A1A1A' : 'white',
                  opacity: 0.9,
                },
              }}
            >
              {t('tuner.openTuner')}
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12} sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {/* Capo Helper Button */}
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={() => navigate('/app/capo-helper')}
              startIcon={<CapoIcon />}
              sx={{
                flex: 1,
                py: 2,
                backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1A1A1A' : 'white',
                color: (theme) => theme.palette.mode === 'dark' ? 'white' : 'primary.main',
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1A1A1A' : 'white',
                  opacity: 0.9,
                },
              }}
            >
              {t('capoHelper.title')}
            </Button>
            
            {/* Church Code Card - Only visible for admins and leaders */}
            {user && (user.role === 'admin' || user.role === 'leader') && churchData?.confirmationCode && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 2,
                  borderRadius: 1,
                  bgcolor: 'primary.light',
                  color: 'primary.contrastText',
                  boxShadow: 1,
                  flex: 1,
                  minWidth: 0, // Allows the box to shrink below its content size if needed
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, overflow: 'hidden' }}>
                  <Typography variant="body1" sx={{ fontWeight: 'medium', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    <strong>{t('settings.church.confirmationCode.description')}: {churchData.confirmationCode}</strong>
                  </Typography>
                  <Tooltip title={t('settings.church.confirmationCode.copyTooltip')}>
                    <IconButton
                      onClick={() => {
                        navigator.clipboard.writeText(churchData.confirmationCode);
                        toast.success(t('settings.church.confirmationCode.copied'));
                      }}
                      size="small"
                      sx={{ color: 'primary.contrastText', ml: 1, flexShrink: 0 }}
                    >
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            )}
          </Box>
        </Grid>

        {/* Most Used Songs */}
        <Grid item xs={12}>
          <Card sx={{
            height: '100%',
            bgcolor: (theme) => theme.palette.mode === 'dark' ? '#1A1A1A' : 'white',
            '& .MuiCardContent-root': {
              paddingBottom: 0
            }
          }}>
            <CardContent>
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between', 
                  mb: 2,
                  cursor: 'pointer'
                }}
                onClick={() => setTopSongsExpanded(!topSongsExpanded)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <QueueMusicIcon color="primary" />
                  <Typography variant="h6" sx={{ ml: 1 }}>
                    {t('dashboard.mostUsedSongs')}
                  </Typography>
                </Box>
                <IconButton
                  aria-expanded={topSongsExpanded}
                  aria-label="show more"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent triggering the parent onClick
                    setTopSongsExpanded(!topSongsExpanded);
                  }}
                >
                  {topSongsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
              
              <Collapse in={topSongsExpanded} timeout="auto" unmountOnExit>
                {loading ? (
                  [...Array(3)].map((_, i) => (
                    <Box key={i} sx={{ mb: 2 }}>
                      <Skeleton variant="text" width="100%" />
                    </Box>
                  ))
                ) : error ? (
                  <Alert severity="error">{error}</Alert>
                ) : stats.topSongs.length === 0 ? (
                  <Typography color="text.secondary">{t('dashboard.noSongs')}</Typography>
                ) : (
                  <List sx={{ p: 0 }}>
                    {stats.topSongs.map((song, index) => (
                      <React.Fragment key={song._id}>
                        {index > 0 && <Divider sx={{ my: 1 }} />}
                        <ListItem
                          sx={{
                            px: 0,
                            cursor: 'pointer',
                            '&:hover': {
                              bgcolor: 'action.hover',
                            },
                            transition: 'background-color 0.2s',
                            borderRadius: 1,
                          }}
                          onClick={() => handleSongClick(song)}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <Typography color="primary" variant="body2">
                              {index + 1}
                            </Typography>
                          </ListItemIcon>
                          <ListItemText primary={song.title} />
                          <Chip
                            label={`${song.usageCount}x`}
                            size="small"
                            sx={{
                              bgcolor: 'primary.main',
                              color: 'white',
                              fontSize: '0.75rem'
                            }}
                          />
                        </ListItem>
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </Collapse>
            </CardContent>
            <CardActions sx={{ justifyContent: 'center', pt: 0, pb: 1 }}>
              <Button
                size="small"
                onClick={() => setTopSongsExpanded(!topSongsExpanded)}
                startIcon={topSongsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              >
                {topSongsExpanded ? t('common.showLess') : t('common.showMore')}
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {/* Birthdays Section */}
        <Grid item xs={12}>
          <BirthdayCard
            birthdays={birthdays}
            loading={birthdaysLoading}
            error={birthdaysError}
          />
        </Grid>

        {/* Upcoming Services */}
        <Grid item xs={12}>
          <UpcomingServices
            upcomingServices={upcomingServices}
            teamSchedule={stats.teamSchedule}
            loading={loading}
            error={error}
          />
        </Grid>

      </Grid>

      <ServiceForm
        open={isServiceFormOpen}
        onClose={() => setIsServiceFormOpen(false)}
        onSubmit={handleServiceSubmit}
        customFetch={customFetch}
      />

      <ProUpgradeModal
        open={proModalOpen}
        onClose={() => setProModalOpen(false)}
      />
    </Box>
  );
};

export default Dashboard;
